/* eslint-disable react/prop-types */
import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import {
  Card,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDCircularLoader from "components/MDCircularLoader";
import DisplayImage from "components/DisplayImage";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DeleteModal from "examples/DeleteModal";
import PageShell from "components/common/PageShell";
import ListEmptyState from "components/common/ListEmptyState";
import StatusChip from "components/common/StatusChip";
import { ActionsCellRenderer } from "utils/helpers";
import DataTable from "../pr-management/components/DataTable";
import FileUpload from "../releases/components/FormField/FileUpload";

import { LIST_OFFERINGS } from "graphql/queries/listOfferings";
import { CREATE_OFFERING } from "graphql/mutations/createOffering";
import { UPDATE_OFFERING } from "graphql/mutations/updateOffering";
import { DELETE_OFFERING } from "graphql/mutations/deleteOffering";
import EditIcon from "assets/images/icons/editIcon.svg";
import DeleteIcon from "assets/images/icons/deleteIcon.svg";

const OFFERING_TYPES = [
  "PR",
  "PODCAST",
  "STAGE_APPEARANCE",
  "CLIPPING",
  "TV_NEWS",
  "ORGANIC_ARTICLE",
  "SEO_BACKLINK",
  "LISTICLE",
  "LINK_INSERT",
];
const TARGETS = ["AGENCY", "BRAND"];
const CURRENCIES = ["INR", "USD"];
const STATUSES = ["UPCOMING", "ACTIVE", "EXPIRED"];

const OFFERING_STATUS_OPTIONS = [
  { value: "UPCOMING", label: "Upcoming", background: "#FF9900" },
  { value: "ACTIVE", label: "Active", background: "#47BC4C" },
  { value: "EXPIRED", label: "Expired", background: "#747474" },
];

const cell = (value: any) => (
  <MDTypography sx={{ fontSize: "14px", fontWeight: 400 }}>
    {value ?? "-"}
  </MDTypography>
);

const emptyForm = {
  title: "",
  description: "",
  offeringType: "PR",
  target: ["BRAND"] as string[],
  price: "",
  offerPrice: "",
  currency: "USD",
  offeringStatus: "ACTIVE",
  image: "",
  expireAt: "",
};

const toDateInput = (v: any) =>
  v ? new Date(v).toISOString().slice(0, 10) : "";

function Offerings(): JSX.Element {
  const { data, loading, refetch } = useQuery(LIST_OFFERINGS, {
    fetchPolicy: "network-only",
    onError: (err) => toast.error(err?.message || "Failed to load offerings"),
  });
  const [createOffering, { loading: creating }] = useMutation(CREATE_OFFERING);
  const [updateOffering, { loading: updating }] = useMutation(UPDATE_OFFERING);
  const [deleteOffering, { loading: deleting }] = useMutation(DELETE_OFFERING);

  const [openForm, setOpenForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteConf, setDeleteConf] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const saving = creating || updating;
  const set = (k: string) => (e: any) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setOpenForm(true);
  };

  const openEdit = (o: any) => {
    setForm({
      title: o.title || "",
      description: o.description || "",
      offeringType: o.offeringType || "PR",
      target: o.target || [],
      price: o.price != null ? String(o.price) : "",
      offerPrice: o.offerPrice != null ? String(o.offerPrice) : "",
      currency: o.currency || "USD",
      offeringStatus: o.offeringStatus || "ACTIVE",
      image: o.image || "",
      expireAt: toDateInput(o.expireAt),
    });
    setEditId(o.id);
    setOpenForm(true);
  };

  const closeForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setOpenForm(false);
  };

  const submit = async () => {
    if (!form.title || !form.price) {
      toast.error("Title and price are required");
      return;
    }
    const params: any = {
      title: form.title,
      description: form.description || undefined,
      offeringType: form.offeringType,
      target: form.target,
      price: parseFloat(form.price),
      offerPrice: form.offerPrice ? parseFloat(form.offerPrice) : undefined,
      currency: form.currency,
      offeringStatus: form.offeringStatus,
      image: form.image || undefined,
      expireAt: form.expireAt ? new Date(form.expireAt).toISOString() : undefined,
    };
    try {
      if (editId) {
        await updateOffering({ variables: { params: { id: editId, ...params } } });
        toast.success("Offering updated");
      } else {
        await createOffering({ variables: { params } });
        toast.success("Offering created");
      }
      closeForm();
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save offering");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOffering({ variables: { id } });
      toast.success("Offering deleted");
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete offering");
    }
  };

  const offerings = data?.listOfferings || [];

  const columns = [
    {
      Header: "Image",
      accessor: "image",
      Cell: ({ value }: any) =>
        value ? (
          <DisplayImage
            src={value}
            height="36px"
            width="60px"
            styles={{ borderRadius: 4, objectFit: "cover" }}
          />
        ) : (
          cell("-")
        ),
    },
    { Header: "Title", accessor: "title", align: "left", Cell: ({ value }: any) => cell(value) },
    { Header: "Type", accessor: "offeringType", Cell: ({ value }: any) => cell(value) },
    {
      Header: "Target",
      accessor: "target",
      Cell: ({ value }: any) => cell((value || []).join(", ")),
    },
    {
      Header: "Price",
      accessor: "price",
      Cell: ({ row }: any) =>
        cell(
          `${row.original.offerPrice ?? row.original.price} ${row.original.currency || ""}`
        ),
    },
    {
      Header: "Status",
      accessor: "offeringStatus",
      Cell: ({ value }: any) =>
        value ? <StatusChip value={value} options={OFFERING_STATUS_OPTIONS} width={120} /> : cell("-"),
    },
    {
      Header: "Actions",
      accessor: "id",
      align: "right",
      Cell: ({ row }: any) => (
        <ActionsCellRenderer
          actions={[
            { imgIcon: EditIcon, onClick: () => openEdit(row.original) },
            { imgIcon: DeleteIcon, onClick: () => setDeleteConf(row.original.id) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {(saving || deleting) && <MDCircularLoader overlayloader startLoader />}
      <DashboardNavbar
        text="Add Offering"
        onsubmit={!openForm && openCreate}
      />
      <PageShell openForm={openForm}>
        {openForm ? (
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <MDTypography variant="h5">
                {editId ? "Edit Offering" : "New Offering"}
              </MDTypography>
              <IconButton onClick={closeForm} size="small">
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Title" value={form.title} onChange={set("title")} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Type" value={form.offeringType} onChange={set("offeringType")} fullWidth>
                  {OFFERING_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={set("description")}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Target audience"
                  value={form.target}
                  onChange={(e: any) =>
                    setForm((f) => ({
                      ...f,
                      target:
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value,
                    }))
                  }
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) => (selected as string[]).join(", "),
                  }}
                  fullWidth
                >
                  {TARGETS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Image&nbsp;
                  <MDTypography variant="caption" color="text">
                    (JPEG, PNG, SVG — max 3MB)
                  </MDTypography>
                </MDTypography>
                <MDBox
                  mt={0.5}
                  sx={{
                    border: "1px dashed #d2d6da",
                    borderRadius: "8px",
                    minHeight: "150px",
                    p: 1,
                  }}
                >
                  <FileUpload
                    value={form.image}
                    onChange={(url: any) =>
                      setForm((f) => ({ ...f, image: url || "" }))
                    }
                  />
                </MDBox>
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField label="Price" type="number" value={form.price} onChange={set("price")} fullWidth />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField label="Offer price" type="number" value={form.offerPrice} onChange={set("offerPrice")} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Currency"
                  value={form.currency}
                  onChange={set("currency")}
                  fullWidth
                  SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 240 } } } }}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Status"
                  value={form.offeringStatus}
                  onChange={set("offeringStatus")}
                  fullWidth
                  SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 240 } } } }}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Expires at"
                  type="date"
                  value={form.expireAt}
                  onChange={set("expireAt")}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <MDBox mt={3} display="flex" gap={1} justifyContent="flex-end">
              <MDButton variant="outlined" color="dark" onClick={closeForm}>
                Cancel
              </MDButton>
              <MDButton color="info" disabled={saving} onClick={submit}>
                {saving ? "Saving…" : editId ? "Update offering" : "Create offering"}
              </MDButton>
            </MDBox>
          </Card>
        ) : loading || offerings.length ? (
          <DataTable
            loading={loading}
            table={{ columns, rows: offerings }}
            showHeader
            entriesPerPage={{ defaultValue: 25 }}
          />
        ) : (
          <ListEmptyState message="No offerings found" />
        )}
      </PageShell>
      {deleteConf && (
        <DeleteModal
          onClose={() => setDeleteConf(null)}
          handleSubmit={() => handleDelete(deleteConf)}
        />
      )}
    </>
  );
}

export default Offerings;
